import { useMemo } from 'react';
import styles from './AviosCurrency.module.css';
import { cva } from 'class-variance-authority';
import AviosCurrencySymbol, {
  type AviosCurrencySymbolProps,
} from '../AviosCurrencySymbol';
import Icon, { IconSize } from '../Icon/Icon';

const aviosCurrency = cva(styles['avios-currency'], {
  variants: {
    styleVariant: {
      primary: styles['avios-currency--primary'],
      secondary: styles['avios-currency--secondary'],
      white: styles['avios-currency--white'],
      onSpend: styles['avios-currency--on-spend'],
      onCollect: styles['avios-currency--on-collect'],
      accentPrimary: styles['avios-currency--accent-primary'],
      accentSecondary: styles['avios-currency--accent-secondary'],
    },
    size: {
      xl: styles['avios-currency--xl'],
      lg: styles['avios-currency--lg'],
      md: styles['avios-currency--md'],
      sm: styles['avios-currency--sm'],
      xs: styles['avios-currency--xs'],
      '2xs': styles['avios-currency--2xs'],
      '3xs': styles['avios-currency--3xs'],
      '4xs': styles['avios-currency--4xs'],
      '5xs': styles['avios-currency--5xs'],
    },
    isStrikethrough: {
      true: styles['avios-currency--strikethrough'],
      false: '',
    },
  },
  defaultVariants: {
    styleVariant: 'accentSecondary',
  },
});

export interface AviosCurrencyProps
  extends Omit<AviosCurrencySymbolProps, 'styleVariant'> {
  children?: number;
  /**
   * The style variant of the Avios currency
   */
  styleVariant?: AviosCurrencySymbolProps['styleVariant'];
  /**
   * The size of the Avios currency
   */
  size?: 'xl' | 'lg' | 'md' | 'sm' | 'xs' | '2xs' | '3xs' | '4xs' | '5xs';
  /**
   * Whether to display the currency name " Avios"
   */
  hasCurrencyName?: boolean;
  /**
   * The decorator to display
   */
  decorator?: 'link' | 'dropdown';
  /**
   * Whether to display the Avios currency as a strikethrough
   */
  isStrikethrough?: boolean;
}

export function getIconSize(size: AviosCurrencyProps['size']): IconSize {
  switch (size) {
    case 'sm':
      return '0.75x';
    case '2xs':
    case '3xs':
    case '4xs':
    case '5xs':
      return '0.5x';
    default:
      return '1x';
  }
}

export const AviosCurrency = ({
  children = 0,
  styleVariant = 'accentSecondary',
  kind = 'balance',
  size = 'xl',
  hasCurrencyName = false,
  decorator,
  isStrikethrough = false,
  ...props
}: AviosCurrencyProps) => {
  const formattedChildren = new Intl.NumberFormat('en-GB', {
    style: 'decimal',
    maximumFractionDigits: 0,
  }).format(children);

  const { isLink, isDropdown } = useMemo(() => {
    if (decorator === 'link') {
      return { isLink: true, isDropdown: false };
    }
    if (decorator === 'dropdown') {
      return { isLink: false, isDropdown: true };
    }
    return { isLink: false, isDropdown: false };
  }, [decorator]);

  const iconSize = useMemo(() => getIconSize(size), [size]);

  return (
    <span className={aviosCurrency({ styleVariant, size, isStrikethrough })}>
      <AviosCurrencySymbol {...props} styleVariant={styleVariant} kind={kind} />
      <span className={styles.aviosCurrency__points}>{formattedChildren}</span>
      {isLink && (
        <Icon iconName="chevron-right" iconSize={iconSize} data-testid="icon" />
      )}
      {isDropdown && (
        <Icon
          iconName="angles-up-down"
          iconSize={iconSize}
          data-testid="icon"
        />
      )}
      {hasCurrencyName && (
        <span className={styles['avios-currency__name']}>Avios</span>
      )}
    </span>
  );
};

export default AviosCurrency;
